/*
 * argile-catalog-header.js
 *
 * 1) Déplace la barre de contrôles native (.facets-toggle « Affiner ma recherche »
 *    + .facets-block-wrapper--horizontal « Trier par ») dans la colonne droite de
 *    l'en-tête catalogue. Un MutationObserver redéplace le bandeau frais à chaque
 *    re-rendu (AJAX des filtres) et retire l'ancien.
 *
 * 2) Recherche libre « Mot clé ou référence » (modale filtres) : interroge la
 *    recherche Shopify NATIVE en AJAX (/search?q=…&type=product, TOUTES les pages)
 *    et injecte les CARTES dans le <ul> du catalogue — on garde la grille catalogue
 *    (mêmes colonnes, même card-size, mêmes cartes _product-card) ; seules les <li>
 *    changent. On affiche tout → bouton « Voir la suite » masqué. Recherche
 *    server-side, instantanée, DOM léger (on ne rend que les résultats).
 *    Vider le champ restaure la grille d'origine (snapshot pris avant la 1re
 *    recherche). Un deep-link (ex. footer) /collections/all?q=nuancier pré-remplit
 *    le champ et lance la recherche au chargement. Le mot-clé est abandonné quand
 *    un filtre natif est appliqué (le serveur re-rend la section catalogue).
 *
 *    Le swap DOM déclenche le MutationObserver : on garde l'état (renderedQuery /
 *    pendingQuery) + un marqueur [data-argile-search] sur la grille injectée pour
 *    éviter toute boucle et toute restauration au-dessus d'un re-rendu serveur.
 *
 * Toutes les écritures DOM (textContent) sont gardées (only-if-changed) pour ne
 * pas relancer l'observer en boucle.
 */
(() => {
  const SLOT = '.argile-catalog-header__controls';
  const KEYWORD_INPUT = '[data-argile-keyword]';
  // Deep-link : un lien (ex. footer) vers /collections/all?q=nuancier pré-remplit
  // le champ mot-clé et lance la recherche AJAX au chargement.
  const KEYWORD_PARAM = 'q';

  /* ---------- 1. Déplacement de la barre native ---------- */
  const moveControls = () => {
    const slot = document.querySelector(SLOT);
    if (!slot) return;

    const toggle = document.querySelector(
      '.facets-toggle:not([data-argile-moved])'
    );
    const bar = document.querySelector(
      '.facets-block-wrapper--horizontal:not([data-argile-moved])'
    );
    if (!toggle && !bar) return;

    slot.querySelectorAll('[data-argile-moved]').forEach((el) => el.remove());

    if (toggle) {
      toggle.setAttribute('data-argile-moved', 'true');
      slot.appendChild(toggle);
    }
    if (bar) {
      bar.setAttribute('data-argile-moved', 'true');
      slot.appendChild(bar);
    }
  };

  /* ---------- 2. Recherche mot-clé : grille + compteur + badge ---------- */
  let keyword = '';
  let kwTimer = null;

  const setText = (el, value) => {
    if (el && el.textContent.trim() !== String(value)) el.textContent = value;
  };

  // Nb de valeurs de filtres natifs actives = nb de params `filter.` dans l'URL.
  const nativeFilterCount = () =>
    (window.location.search.match(/[?&]filter\./g) || []).length;

  // Tous les compteurs `[total]` de la page (hero + sous-titre catalogue).
  const countSpans = () => document.querySelectorAll('.argile-total-count');

  // Compteurs quand AUCUN mot-clé : résultat filtré natif, sinon total.
  const updateFacetCount = () => {
    const spans = countSpans();
    if (!spans.length) return;
    if (!/[?&]filter\./.test(window.location.search)) {
      spans.forEach((s) => setText(s, s.dataset.total || s.textContent.trim()));
      return;
    }
    const status = document.querySelector(
      `${SLOT} .products-count-wrapper [role="status"]`
    );
    if (status) {
      const n = status.textContent.replace(/[^\d]/g, '');
      if (n !== '') spans.forEach((s) => setText(s, n));
    }
  };

  // Badge « Affiner ma recherche » = filtres natifs + mot-clé actif.
  const updateBadge = () => {
    const btn = document.querySelector(`${SLOT} .facets-toggle__button`);
    if (!btn) return;
    const total = nativeFilterCount() + (keyword.trim() ? 1 : 0);
    let bubble = btn.querySelector('.filter-count-bubble');

    if (total > 0) {
      if (!bubble) {
        bubble = document.createElement('span');
        bubble.className = 'filter-count-bubble';
        bubble.innerHTML =
          '<span class="filter-count-bubble__background"></span>' +
          '<span class="filter-count-bubble__text"></span>';
        btn.appendChild(bubble);
      }
      setText(
        bubble.querySelector('.filter-count-bubble__text') || bubble,
        total
      );
      if (bubble.style.display === 'none') bubble.style.display = '';
    } else if (bubble && bubble.style.display !== 'none') {
      bubble.style.display = 'none';
    }
  };

  /* ----- Index de références (N° Couleur → nom de teinte) ----- */
  // La recherche Shopify native n'indexe PAS les métafields (custom.numero_couleur
  // / ref_teinte). Une réf comme « C101 » ne remonte donc jamais. On charge un
  // petit index réf→nom (asset argile-teintes-ref.json, généré depuis grouped.csv)
  // et on RÉSOUT la réf en nom de teinte : la recherche par titre, elle, sait la
  // retrouver. L'expansion s'ajoute à la requête brute (recherche par nom inchangée).
  let refIndex = null; // null = pas encore chargé ; {} = chargé (vide/échec)
  let famIndex = null; // idem : NORM(famille) -> [noms de teintes] (custom.familles)
  const REF_LIKE = /^[a-z]{1,3}\d/i; // ex. C101, V26, T4… (lettre(s) puis chiffre)
  // Normalisation commune (maj, sans accents, alphanum) — clé de l'index familles.
  const normFam = (s) =>
    s.toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Z0-9]/g, '');

  // Charge un index JSON (réf ou familles) et relance si un mot-clé concerné a déjà
  // été saisi avant la fin du fetch (course au boot). `test` filtre ce cas.
  const loadIndex = (attr, get, set, test) => {
    if (get() !== null) return; // déjà chargé ou en cours
    const src = document.querySelector(KEYWORD_INPUT)?.dataset[attr];
    if (!src) return;
    set({}); // marqueur « en cours » : évite tout re-fetch
    fetch(src)
      .then((r) => r.json())
      .then((map) => {
        set(map || {});
        const q = (document.querySelector(KEYWORD_INPUT)?.value || '').trim();
        if (q && test(q)) {
          renderedQuery = null;
          pendingQuery = null;
          refresh();
        }
      })
      .catch(() => set({}));
  };

  const loadRefIndex = () => {
    loadIndex('argileRefSrc', () => refIndex, (v) => (refIndex = v), (q) => REF_LIKE.test(q));
    loadIndex('argileFamillesSrc', () => famIndex, (v) => (famIndex = v), (q) => !!(famIndex && famIndex[normFam(q)]));
  };

  // Traduit un mot-clé en la liste des requêtes NATIVES à lancer.
  // - Mot-clé « réf » (lettre(s)+chiffre, ex. c101, v26, c10…) : on cherche
  //   UNIQUEMENT par NOM de teinte résolu (réf == ou commence par le mot-clé).
  //   On n'inclut PAS la requête brute : la recherche native ne trouve pas la réf
  //   (métafield non indexé) et RENVOIE TOUTE LA GRILLE en repli → pollution.
  //   Réf inconnue → [] → état « aucun résultat » (plutôt que tout le catalogue).
  // - Sinon (mot-clé « nom ») : recherche native par nom → ['bleu'] + si le mot-clé
  //   EST une famille de couleur (ex. « kaki », « rose »), on AJOUTE les noms de
  //   teintes de cette famille. Le filtre couleur repose sur custom.familles, non
  //   indexé par la recherche native → on résout famille→noms comme pour les réfs,
  //   et la recherche par titre retrouve chaque teinte. Union avec la requête brute.
  const REF_FANOUT_MAX = 30; // garde-fou : préfixe très court (ex. « c1 »)
  const FAM_FANOUT_MAX = 60; // couvre la plus grosse famille (Bleu ~46)
  const expandQueries = (q) => {
    // Réf (lettre+chiffre) : noms résolus SEULS (pas de requête brute → la native
    // ne trouve pas la réf et renverrait toute la grille en repli).
    if (refIndex && REF_LIKE.test(q)) {
      const key = q.toUpperCase().replace(/\s+/g, '');
      const names = [];
      const seen = new Set();
      for (const ref in refIndex) {
        if (ref === key || ref.startsWith(key)) {
          const name = refIndex[ref];
          if (name && !seen.has(name)) {
            seen.add(name);
            names.push(name);
          }
        }
      }
      return names.slice(0, REF_FANOUT_MAX);
    }
    // Mot-clé « nom » : requête brute + expansion famille (match exact normalisé).
    const out = [q];
    const seen = new Set([q]);
    const famNames = famIndex && famIndex[normFam(q)];
    if (famNames) {
      for (const name of famNames) {
        if (!seen.has(name)) {
          seen.add(name);
          out.push(name);
        }
      }
    }
    return out.slice(0, FAM_FANOUT_MAX + 1);
  };

  /* ----- Recherche AJAX native injectée dans la grille catalogue ----- */
  const RESULTS_ID = 'ResultsList'; // wrapper grille (identique collection/search)
  const SEARCH_MARK = 'data-argile-search'; // marque la grille issue d'une recherche

  let renderedQuery = null; // mot-clé actuellement affiché dans la grille (null = origine)
  let pendingQuery = null; // mot-clé dont la requête est en cours
  let searchSeq = 0; // garde anti-réponse périmée / annulation
  let originalHTML = null; // snapshot de #ResultsList avant la 1re recherche

  const resultsListEl = () => document.getElementById(RESULTS_ID);

  const searchUrl = (q, page) => {
    const root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    let url =
      `${root}search?q=${encodeURIComponent(q)}` +
      '&type=product&options[prefix]=last' +
      (page && page > 1 ? `&page=${page}` : '');
    // Propage le contexte d'aperçu pour que le fetch rende avec le BON thème
    // (sinon en preview on tape le thème PUBLIÉ → ancien rendu de carte).
    const cur = new URLSearchParams(window.location.search);
    for (const k of ['preview_theme_id', '_fd', 'pb', '_ab', '_bt']) {
      if (cur.has(k)) url += `&${k}=${encodeURIComponent(cur.get(k))}`;
    }
    return url;
  };

  // Grille interne (le <ul> du catalogue : on garde ses classes/colonnes/card-size).
  const catalogGrid = () => document.querySelector(`#${RESULTS_ID} .product-grid`);

  // Récupère le document HTML d'une page de résultats /search.
  const fetchSearchDoc = (q, page) =>
    fetch(searchUrl(q, page))
      .then((res) => res.text())
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));

  // Met à jour les compteurs `[total]` selon l'état (recherche / filtre / total).
  const updateCount = () => {
    if (keyword.trim()) {
      const n = document.querySelectorAll(
        `#${RESULTS_ID} .product-grid__item`
      ).length;
      countSpans().forEach((s) => setText(s, n));
    } else {
      updateFacetCount();
    }
  };

  // Restaure la grille d'origine (collection / filtre natif courant).
  const restoreOriginal = () => {
    const live = resultsListEl();
    if (originalHTML != null && live) live.outerHTML = originalHTML;
    originalHTML = null;
    renderedQuery = null;
  };

  // Exclut les produits de préparation (tag « preparation », marqués côté
  // search-results.liquid) : la prépa ne doit pas remonter dans la recherche
  // du catalogue. La page /search globale, elle, reste inchangée.
  const keep = (li) => !li.hasAttribute('data-preparation');

  // Récupère TOUTES les cartes d'une requête native (toutes les pages).
  // Renvoie { items, doc } ou null si la réponse est périmée (seq dépassé).
  const fetchAllItems = async (q, seq) => {
    const doc1 = await fetchSearchDoc(q, 1);
    if (seq !== searchSeq) return null; // réponse périmée → ignorée
    const grid1 = doc1.querySelector('.product-grid');
    const lastPage = grid1
      ? parseInt(grid1.getAttribute('data-last-page') || '1', 10)
      : 1;
    const items = grid1
      ? [...grid1.querySelectorAll('.product-grid__item')].filter(keep)
      : [];

    // Pages suivantes en parallèle, assemblées dans l'ordre.
    if (lastPage > 1) {
      const docs = await Promise.all(
        Array.from({ length: lastPage - 1 }, (_, i) => fetchSearchDoc(q, i + 2))
      );
      if (seq !== searchSeq) return null;
      docs.forEach((d) => {
        const g = d.querySelector('.product-grid');
        if (g) items.push(...[...g.querySelectorAll('.product-grid__item')].filter(keep));
      });
    }
    return { items, doc: doc1 };
  };

  // Lance la recherche native (toutes les pages) et injecte les CARTES dans le
  // <ul> du catalogue — on conserve la grille catalogue (3 col., même card-size),
  // seules les <li> changent. On affiche tout → le bouton « Voir la suite » est masqué.
  // Le mot-clé est étendu (expandQueries) : requête brute par NOM + les teintes
  // retrouvées par RÉFÉRENCE. Les cartes de toutes les requêtes sont fusionnées
  // (dédoublonnées par URL produit).
  const runSearch = async (q) => {
    pendingQuery = q;
    const seq = ++searchSeq;
    if (originalHTML == null) {
      const live = resultsListEl();
      if (live) originalHTML = live.outerHTML; // snapshot avant 1re recherche
    }
    try {
      const queries = expandQueries(q);
      const merged = [];
      const seenHref = new Set();
      let lastDoc = null;

      // Requêtes en parallèle (un préfixe de réf peut résoudre plusieurs noms),
      // puis fusion dans l'ordre des requêtes, dédoublonnée par URL produit.
      const results = await Promise.all(queries.map((query) => fetchAllItems(query, seq)));
      if (seq !== searchSeq) return; // réponse périmée → on abandonne
      for (const res of results) {
        if (res === null) return;
        lastDoc = res.doc;
        for (const li of res.items) {
          const a = li.querySelector('a[href]');
          const href = a ? a.getAttribute('href').split('?')[0] : null;
          if (href && seenHref.has(href)) continue; // déjà présent (union des requêtes)
          if (href) seenHref.add(href);
          merged.push(li);
        }
      }

      const ul = catalogGrid();
      if (!ul) return;

      if (merged.length) {
        ul.replaceChildren(
          ...merged.map((li) => document.importNode(li, true))
        );
      } else {
        // Aucun résultat : message vide du thème (sinon grille vide).
        const empty = lastDoc && lastDoc.querySelector('.main-collection-grid__empty');
        ul.replaceChildren(
          empty
            ? document.importNode(empty, true)
            : document.createTextNode('')
        );
      }

      resultsListEl()?.setAttribute(SEARCH_MARK, q);
      // On affiche tout → masque le bouton « Voir la suite » du catalogue.
      // (style.display, car [hidden] est neutralisé par le CSS .argile-load-more.)
      const loadMore = document.querySelector('[data-argile-load-more]');
      if (loadMore) loadMore.style.display = 'none';
      renderedQuery = q;
    } catch (e) {
      /* silencieux : on laisse la grille en place */
    } finally {
      if (seq === searchSeq) pendingQuery = null;
      updateCount();
      updateBadge();
    }
  };

  // Cœur : appelé par l'observer (refresh) et la saisie. Idempotent.
  const applyKeyword = () => {
    const q = keyword.trim();

    if (q === '') {
      // Champ vide : on restaure UNIQUEMENT au-dessus de NOTRE grille de recherche.
      // (Un re-rendu serveur — filtre natif — n'a pas le marqueur → on n'écrase pas.)
      const live = resultsListEl();
      if (live && live.hasAttribute(SEARCH_MARK)) {
        restoreOriginal();
      } else {
        originalHTML = null;
        renderedQuery = null;
      }
      pendingQuery = null;
      searchSeq++; // annule toute requête en vol
      updateFacetCount();
      updateBadge();
      return;
    }

    // Déjà affiché ou requête en cours pour ce mot-clé → rien à refaire
    // (sécurise la ré-entrée déclenchée par le MutationObserver après le swap).
    if (q === renderedQuery || q === pendingQuery) {
      updateCount();
      updateBadge();
      return;
    }

    runSearch(q);
    updateBadge();
  };

  // Synchronise le mot-clé avec l'input réel (persiste sur scroll, vide après
  // re-rendu AJAX d'un filtre natif) puis ré-applique tout.
  const refresh = () => {
    moveControls();
    loadRefIndex();
    keyword = document.querySelector(KEYWORD_INPUT)?.value || '';
    applyKeyword();
  };

  // Pré-remplit l'input depuis le query param `q` (deep-link footer, etc.).
  // Ne touche pas un input déjà rempli (saisie utilisateur prioritaire).
  const seedKeywordFromURL = () => {
    const val = (
      new URLSearchParams(window.location.search).get(KEYWORD_PARAM) || ''
    ).trim();
    if (!val) return;
    const input = document.querySelector(KEYWORD_INPUT);
    if (input && !input.value) input.value = val;
  };

  /* ---------- Boot + ré-inits ---------- */
  const boot = () => {
    seedKeywordFromURL();
    refresh();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', refresh);

  // Filtre natif appliqué : le serveur re-rend la grille catalogue (filtrée).
  // On abandonne la recherche mot-clé (état + champ) sans rien restaurer, pour
  // ne jamais écraser la grille fraîchement rendue (filter:update = events.js).
  document.addEventListener('filter:update', () => {
    originalHTML = null;
    renderedQuery = null;
    pendingQuery = null;
    searchSeq++;
    const input = document.querySelector(KEYWORD_INPUT);
    if (input) input.value = '';
    keyword = '';
    // Resync du compteur custom (.argile-total-count) — il est HORS de la section
    // re-rendue par Horizon. À ce stade l'URL est déjà à jour (facets.js pushState
    // AVANT de dispatcher filter:update) : au « Tout effacer », l'URL est propre →
    // updateFacetCount lit data-total (= all_products_count, total réel) au lieu de
    // rester figé sur le compte filtré du deep-link. Le MutationObserver ne suffit
    // pas : la View Transition du re-rendu masque les mutations. rAF = laisse la
    // grille filtrée se poser pour le cas filtre→filtre (lecture du compteur natif).
    requestAnimationFrame(() => {
      updateFacetCount();
      updateBadge();
    });
  });

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      refresh();
    });
  };
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
  });

  /* ---------- Saisie utilisateur (délégation) ---------- */
  document.addEventListener('input', (event) => {
    const input = event.target.closest && event.target.closest(KEYWORD_INPUT);
    if (!input) return;
    keyword = input.value;
    clearTimeout(kwTimer);
    kwTimer = setTimeout(applyKeyword, 120);
  });

  document.addEventListener('keydown', (event) => {
    const input = event.target.closest && event.target.closest(KEYWORD_INPUT);
    if (!input || event.key !== 'Enter') return;
    event.preventDefault();
    keyword = input.value;
    applyKeyword();
  });

  document.addEventListener('click', (event) => {
    const clear =
      event.target.closest &&
      event.target.closest('[data-argile-keyword-clear]');
    if (!clear) return;
    const input = clear
      .closest('.argile-filter-search')
      ?.querySelector(KEYWORD_INPUT);
    if (input) input.value = '';
    keyword = '';
    applyKeyword();
    if (input) input.focus();
  });
})();
